import { formatDate } from "@/lib/utils";
import React, { useState, useMemo, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  FileText,
  Download,
  Filter,
  Users,
  Store,
  Calendar,
  Layers,
  CheckCircle,
  Clock,
  Printer as PrintIcon,
  RefreshCcw,
  Loader2,
  DollarSign,
  PieChart,
  Activity,
  Receipt,
} from "lucide-react";
import apiClient from "../../api/apiClient";
import { API_BASE_URL, API_ENDPOINTS } from "../../api/header";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/components/layout/PageContainer";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ReactSelect, AsyncReactSelect } from "@/components/ui/react-select";

export const CustomerwiseSalesReport = ({
  embedded = false,
  initialCustomerId = "",
  initialCustomerName = "",
  initialStoreId = "",
  onBackToList = null,
} = {}) => {
  const currentStore = useSelector((state) => state.current_store);
  const currencySymbol = currentStore?.default_currency?.symbol || "";

  const resolvedInitialStoreId = initialStoreId
    ? String(initialStoreId)
    : currentStore?.id
      ? String(currentStore.id)
      : "";

  const [filters, setFilters] = useState(() => ({
    customer_id: initialCustomerId ? String(initialCustomerId) : "",
    store_id: resolvedInitialStoreId,
    start_date: "",
    end_date: "",
    min_total: "",
    max_total: "",
    limit: 100,
    sort_by: "total_spent",
    sort_order: "desc",
  }));
  const [selectedCustomer, setSelectedCustomer] = useState(
    initialCustomerId
      ? {
        value: String(initialCustomerId),
        label: initialCustomerName || "Selected Customer",
      }
      : null,
  );
  const [showModal, setShowModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showFilters, setShowFilters] = useState(true);

  const prevStoreIdRef = useRef(currentStore?.id);

  useEffect(() => {
    if (currentStore?.id && currentStore.id !== prevStoreIdRef.current) {
      prevStoreIdRef.current = currentStore.id;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFilters((prev) => ({
        ...prev,
        store_id: String(currentStore.id),
      }));
    }
  }, [currentStore?.id]);



  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    if (!permission) return true;
    return permissions.includes(permission);
  };

  const showToast = (message, type) => {
    if (type === "error") toast.error(message);
    else if (type === "success") toast.success(message);
    else toast(message);
  };

  const summaryStats = useMemo(() => {
    if (!previewData?.data || !Array.isArray(previewData.data)) return null;
    const data = previewData.data;

    return {
      total_spent: data.reduce(
        (sum, row) => sum + (parseFloat(row.total_spent) || 0),
        0,
      ),
      total_customers: data.length,
      total_orders: data.reduce(
        (sum, row) => sum + (parseInt(row.order_count) || 0),
        0,
      ),
      total_outstanding: data.reduce(
        (sum, row) => sum + (parseFloat(row.outstanding) || 0),
        0,
      ),
    };
  }, [previewData]);

  // ==================== QUERIES ====================
  const loadCustomerOptions = async (inputValue) => {
    try {
      const params = { perPage: 15 };
      if (inputValue) {
        params.q = String(inputValue);
      }
      if (filters.store_id) {
        params.registration_store_id = filters.store_id;
      }

      const response = await apiClient.get(`/customer`, {
        params,
      });
      const customersData = response.data.data || [];
      return customersData.map((customer) => ({
        value: String(customer.id),
        label:
          customer.customer_type !== "individual" && customer.company
            ? customer.company
            : customer.full_name,
      }));
    } catch (error) {
      console.error("Error loading customers:", error);
      return [];
    }
  };

  const { data: stores = [] } = useQuery({
    enabled: hasPermission("report customerwise sales view"),
    queryKey: ["stores"],
    queryFn: async () => {
      const response = await apiClient.get(`/store`);
      return response.data.data;
    },
  });

  // ==================== MUTATIONS ====================
  const previewReportMutation = useMutation({
    mutationFn: async (currentFilters) => {
      if (!hasPermission("report customerwise sales view")) {
        throw new Error("You do not have permission to perform this action.");
      }
      const params = {};
      if (currentFilters.customer_id)
        params.customer_id = currentFilters.customer_id;
      if (currentFilters.store_id) params.store_id = currentFilters.store_id;
      if (currentFilters.start_date)
        params.start_date = new Date(currentFilters.start_date).toISOString();
      if (currentFilters.end_date)
        params.end_date = new Date(currentFilters.end_date).toISOString();
      if (currentFilters.min_total !== "")
        params.min_total = Number(currentFilters.min_total);
      if (currentFilters.max_total !== "")
        params.max_total = Number(currentFilters.max_total);
      if (currentFilters.limit) params.limit = Number(currentFilters.limit);
      if (currentFilters.sort_by) params.sort_by = currentFilters.sort_by;
      if (currentFilters.sort_order)
        params.sort_order = currentFilters.sort_order;

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.DATA.CUSTOMERWISE_SALES,
        { params },
      );
      return response.data;
    },
    onSuccess: (data) => {
      setPreviewData(data);
      setShowFilters(false);
      showToast("Report preview loaded successfully!", "success");
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message || "Failed to load report preview",
        "error",
      );
    },
  });

  const generateReportMutation = useMutation({
    mutationFn: async (currentFilters) => {
      if (!hasPermission("report customerwise sales view")) {
        throw new Error("You do not have permission to perform this action.");
      }
      const params = {};
      if (currentFilters.customer_id)
        params.customer_id = currentFilters.customer_id;
      if (currentFilters.store_id) params.store_id = currentFilters.store_id;
      if (currentFilters.start_date)
        params.start_date = new Date(currentFilters.start_date).toISOString();
      if (currentFilters.end_date)
        params.end_date = new Date(currentFilters.end_date).toISOString();
      if (currentFilters.min_total !== "")
        params.min_total = Number(currentFilters.min_total);
      if (currentFilters.max_total !== "")
        params.max_total = Number(currentFilters.max_total);
      if (currentFilters.limit) params.limit = Number(currentFilters.limit);
      if (currentFilters.sort_by) params.sort_by = currentFilters.sort_by;
      if (currentFilters.sort_order)
        params.sort_order = currentFilters.sort_order;

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.CUSTOMERWISE_SALES,
        {
          params,
          responseType: "blob",
        },
      );
      return response.data;
    },
    onSuccess: (data) => {
      const blob = new Blob([data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setShowModal(true);
      showToast("Customerwise sales report generated successfully!", "success");
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message ||
        "Failed to generate customerwise sales report",
        "error",
      );
    },
  });

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleGenerateReport = () => {
    showToast("Generating customerwise sales report...", "info");
    generateReportMutation.mutate(filters);
  };

  const handleClearFilters = () => {
    setShowFilters(true);
    setPreviewData(null);
    setFilters({
      customer_id: "",
      store_id: currentStore?.id ? String(currentStore.id) : "",
      start_date: "",
      end_date: "",
      min_total: "",
      max_total: "",
      limit: 100,
      sort_by: "total_spent",
      sort_order: "desc",
    });
    setSelectedCustomer(null);
  };

  const handleDownloadPDF = () => {
    if (pdfBlobUrl) {
      const link = document.createElement("a");
      link.href = pdfBlobUrl;
      link.download = `customerwise-sales-report-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast("PDF downloaded successfully!", "success");
    }
  };

  const handlePrintPDF = () => {
    if (pdfBlobUrl) {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = pdfBlobUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      };
      showToast("Opening print dialog...", "info");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  const storeOptions = [
    { value: "", label: "All Locations" },
    ...stores.map((store) => ({
      value: String(store.id),
      label: store.name,
    })),
  ];

  const reportBody = (
    <div className="flex flex-col gap-6">
      {embedded && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 rounded-xl bg-card border shadow-sm">
          <div>
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-bold tracking-tight">Customerwise Sales Report</h2>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Analyze sales performance, orders, and outstanding balances across customers.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {onBackToList && (
              <Button variant="outline" size="sm" onClick={onBackToList} className="rounded-lg">
                Back to Customer List
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleClearFilters} className="rounded-lg">
              <RefreshCcw className="mr-1.5 h-4 w-4" /> Reset
            </Button>
            {hasPermission("report customerwise sales view") && (
              <Button
                variant="secondary"
                size="sm"
                className="rounded-lg shadow-sm"
                onClick={handleGenerateReport}
                disabled={generateReportMutation.isPending}
              >
                {generateReportMutation.isPending ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Generating...
                  </>
                ) : (
                  <>
                    <Download className="mr-1.5 h-4 w-4" /> Download PDF
                  </>
                )}
              </Button>
            )}
            {hasPermission("report customerwise sales view") && (
              <Button
                size="sm"
                className="rounded-lg shadow-sm"
                onClick={() => {
                  showToast("Loading report data...", "info");
                  previewReportMutation.mutate(filters);
                }}
                disabled={previewReportMutation.isPending}
              >
                {previewReportMutation.isPending ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Loading...
                  </>
                ) : (
                  <>
                    <FileText className="mr-1.5 h-4 w-4" /> Preview Report
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-8">
        {/* Main Filter Panel */}
        <Card className="shadow-none border border-border/50 overflow-hidden transition-all duration-300">
          <CardHeader className="bg-muted/50 border-b pb-4 pt-4 flex flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-background shadow-sm rounded-lg border">
                <Filter className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <CardTitle className="text-xl">Filtering Criteria</CardTitle>
                <CardDescription className="text-muted-foreground">
                  Define the customer, limits, and date range
                </CardDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="text-xs font-semibold rounded-lg"
            >
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button>
          </CardHeader>

          {showFilters && (
            <CardContent className="p-8">
              <div className="grid md:grid-cols-3 gap-8">
                {/* Date Scope */}
                <div className="space-y-6">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" /> Date Scope
                  </h3>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Start Date
                      </Label>
                      <Input
                        type="datetime-local"
                        className="bg-muted/30 border-border focus:bg-background transition-colors"
                        name="start_date"
                        value={filters.start_date}
                        onChange={handleFilterChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">End Date</Label>
                      <Input
                        type="datetime-local"
                        className="bg-muted/30 border-border focus:bg-background transition-colors"
                        name="end_date"
                        value={filters.end_date}
                        onChange={handleFilterChange}
                      />
                    </div>
                  </div>

                  <div className="pt-4 border-t space-y-4">
                    <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                      <Layers className="h-4 w-4 text-primary" /> Sort & Limit
                    </h3>
                    <div className="grid grid-cols-1 gap-4">
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">Sort By</Label>
                        <Select
                          value={filters.sort_by}
                          onValueChange={(val) =>
                            setFilters((p) => ({ ...p, sort_by: val }))
                          }
                        >
                          <SelectTrigger className="bg-muted/30 border-border">
                            <SelectValue placeholder="Sort By" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="customer_name">
                              Customer Name
                            </SelectItem>
                            <SelectItem value="total_spent">
                              Total Spent
                            </SelectItem>
                            <SelectItem value="order_count">
                              Order Count
                            </SelectItem>
                            <SelectItem value="avg_order_value">
                              Avg Order Value
                            </SelectItem>
                            <SelectItem value="last_purchase_date">
                              Last Purchase Date
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">
                          Sort Order
                        </Label>
                        <Select
                          value={filters.sort_order}
                          onValueChange={(val) =>
                            setFilters((p) => ({ ...p, sort_order: val }))
                          }
                        >
                          <SelectTrigger className="bg-muted/30 border-border">
                            <SelectValue placeholder="Sort Order" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="asc">Ascending</SelectItem>
                            <SelectItem value="desc">Descending</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2 col-span-2">
                        <Label className="text-muted-foreground">
                          Limit Results
                        </Label>
                        <Input
                          type="number"
                          min="1"
                          max="100"
                          className="bg-muted/30 border-border focus:bg-background transition-colors"
                          name="limit"
                          value={filters.limit}
                          onChange={handleFilterChange}
                          placeholder="Max 100"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Entity Filters */}
                <div className="space-y-6">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" /> Entity Scope
                  </h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Customer</Label>
                      <AsyncReactSelect
                        loadOptions={loadCustomerOptions}
                        defaultOptions
                        value={selectedCustomer}
                        onChange={(opt) => {
                          setSelectedCustomer(opt);
                          setFilters((p) => ({
                            ...p,
                            customer_id: opt?.value || "",
                          }));
                        }}
                        placeholder="Search Customers..."
                        isClearable
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Store / Branch
                      </Label>
                      <ReactSelect
                        options={storeOptions}
                        value={
                          storeOptions.find(
                            (opt) => opt.value === String(filters.store_id || ""),
                          ) ||
                          (filters.store_id &&
                            currentStore?.id &&
                            String(filters.store_id) === String(currentStore.id)
                            ? {
                              value: String(currentStore.id),
                              label: currentStore.name,
                            }
                            : null)
                        }
                        onChange={(opt) =>
                          setFilters((p) => ({
                            ...p,
                            store_id: opt?.value || "",
                          }))
                        }
                        placeholder="All Locations"
                        isClearable={Boolean(filters.store_id)}
                      />
                    </div>
                  </div>
                </div>

                {/* Amount Filters */}
                <div className="space-y-6">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-primary" /> Amount
                    Limits
                  </h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Min Total Spent
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        className="bg-muted/30 border-border focus:bg-background transition-colors"
                        name="min_total"
                        value={filters.min_total}
                        onChange={handleFilterChange}
                        placeholder="e.g. 1000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Max Total Spent
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        className="bg-muted/30 border-border focus:bg-background transition-colors"
                        name="max_total"
                        value={filters.max_total}
                        onChange={handleFilterChange}
                        placeholder="e.g. 50000"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {previewData && (
          <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {summaryStats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Total Revenue
                      </p>
                      <h3 className="text-2xl font-bold text-primary">
                        {currencySymbol}
                        {(
                          parseFloat(summaryStats.total_spent) || 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </h3>
                    </div>
                    <div className="p-3 bg-primary/10 text-primary rounded-xl">
                      <DollarSign className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Total Customers
                      </p>
                      <h3 className="text-2xl font-bold text-foreground">
                        {summaryStats.total_customers}
                      </h3>
                    </div>
                    <div className="p-3 bg-muted text-muted-foreground rounded-xl">
                      <Users className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Total Orders
                      </p>
                      <h3 className="text-2xl font-bold text-foreground">
                        {summaryStats.total_orders}
                      </h3>
                    </div>
                    <div className="p-3 bg-amber-100 text-amber-600 rounded-xl">
                      <Receipt className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Total Outstanding
                      </p>
                      <h3 className="text-2xl font-bold text-destructive">
                        {currencySymbol}
                        {(
                          parseFloat(summaryStats.total_outstanding) || 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </h3>
                    </div>
                    <div className="p-3 bg-destructive/10 text-destructive rounded-xl">
                      <Activity className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {previewData.data && (
              <Card className="shadow-none border border-border/50 overflow-hidden bg-background">
                <CardHeader className="bg-muted/50 border-b pb-4 pt-4">
                  <CardTitle className="text-lg">
                    Customer Sales Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead className="text-right">Orders</TableHead>
                        <TableHead className="text-right">Avg Order</TableHead>
                        <TableHead className="text-right">
                          Total Spent
                        </TableHead>
                        <TableHead className="text-right">
                          Outstanding
                        </TableHead>
                        <TableHead className="text-right">
                          Last Purchase
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewData.data.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            className="text-center py-8 text-muted-foreground"
                          >
                            No data found for the selected criteria.
                          </TableCell>
                        </TableRow>
                      ) : (
                        previewData.data.map((row, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">
                              {row.customer_name}
                            </TableCell>
                            <TableCell>
                              <div className="text-xs">
                                {row.email && row.email !== "-" && (
                                  <div className="text-muted-foreground">
                                    {row.email}
                                  </div>
                                )}
                                {row.phone && (
                                  <div className="text-muted-foreground">
                                    {row.phone}
                                  </div>
                                )}
                                {!row.phone &&
                                  (!row.email || row.email === "-") && (
                                    <span className="text-muted-foreground">
                                      -
                                    </span>
                                  )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              {row.order_count}
                            </TableCell>
                            <TableCell className="text-right">
                              {currencySymbol}
                              {(
                                parseFloat(row.avg_order_value) || 0
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                              })}
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              {currencySymbol}
                              {(
                                parseFloat(row.total_spent) || 0
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                              })}
                            </TableCell>
                            <TableCell className="text-right text-destructive">
                              {currencySymbol}
                              {(
                                parseFloat(row.outstanding) || 0
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                              })}
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">
                              {row.last_purchase_date
                                ? formatDate(row.last_purchase_date,)
                                : "-"}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md p-0 overflow-hidden border-none shadow-2xl">
          <div className="h-2 bg-primary" />
          <div className="p-8 space-y-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="h-20 w-20 bg-success/10 text-success rounded-full flex items-center justify-center shadow-inner">
                <CheckCircle className="h-10 w-10" />
              </div>
              <div className="space-y-1">
                <DialogTitle className="text-2xl font-bold">
                  Report Generated!
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  The Customerwise Sales Report is ready based on your specific
                  criteria and is waiting for your action.
                </DialogDescription>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={handleDownloadPDF}
                size="lg"
                className="rounded-xl w-full"
              >
                <Download className="mr-2 h-4 w-4" /> Download
              </Button>
              <Button
                onClick={handlePrintPDF}
                variant="outline"
                size="lg"
                className="rounded-xl w-full"
              >
                <PrintIcon className="mr-2 h-4 w-4" /> Print
              </Button>
            </div>
          </div>
          <div className="bg-muted/50 p-4 text-center border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCloseModal}
              className="font-medium"
            >
              Dismiss
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );

  if (embedded) {
    return reportBody;
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title={
            <div className="flex flex-col">
              <div className="flex items-center gap-3 text-primary mb-2">
                <div className="bg-primary/10 rounded-xl p-1">
                  <FileText className="h-5 w-5" />
                </div>
                <span className="text-xs font-bold uppercase tracking-widest text-primary/80">
                  Performance Analytics
                </span>
              </div>
              <span className="text-3xl font-extrabold tracking-tight">
                Customerwise Sales Report
              </span>
            </div>
          }
          subtitle="Analyze your sales performance across customers with advanced filtering and sorting."
        />
        <PageHeaderActions>
          <Button
            variant="outline"
            size="lg"
            onClick={handleClearFilters}
            className="rounded-xl mr-2"
          >
            <RefreshCcw className="mr-2 h-4 w-4" /> Reset
          </Button>
          {hasPermission("report customerwise sales view") && (
            <Button
              variant="secondary"
              size="lg"
              className="rounded-xl shadow-lg shadow-secondary/20 mr-2"
              onClick={handleGenerateReport}
              disabled={generateReportMutation.isPending}
            >
              {generateReportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Generate PDF
                </>
              )}
            </Button>
          )}
          {hasPermission("report customerwise sales view") && (
            <Button
              size="lg"
              className="rounded-xl shadow-lg shadow-primary/20"
              onClick={() => {
                showToast("Loading report data...", "info");
                previewReportMutation.mutate(filters);
              }}
              disabled={previewReportMutation.isPending}
            >
              {previewReportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  Preview Report
                </>
              )}
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>
      {reportBody}
    </PageContainer>
  );
};

export default CustomerwiseSalesReport;
